const STAGING_MAPBOX_TOKEN =
  'pk.eyJ1IjoiZGRhdmVucG9ydDIzIiwiYSI6ImNtcDM2dnpvajBkZzgycm80MXkza3l6enkifQ.Iqu42lrU5ve6lsbpCYS4Dg'

export const MAPBOX_TOKEN =
  (import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN as string | undefined) || STAGING_MAPBOX_TOKEN

// reverse geocode stays on v5 — it's address-only and works fine
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  const token = MAPBOX_TOKEN
  if (!token) return null
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&limit=1&types=address,place`
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const data = (await res.json()) as { features?: { place_name: string }[] }
    return data.features?.[0]?.place_name ?? null
  } catch {
    return null
  }
}

// Suggestions now come from the Search Box API — supports business/POI names
export interface GeocodeSuggestion {
  placeName: string // display label
  address: string // secondary line (formatted address)
  mapboxId: string // needed for the retrieve step
}

export async function forwardGeocodeSuggestions(
  query: string,
  sessionToken: string,
): Promise<GeocodeSuggestion[]> {
  const token = MAPBOX_TOKEN
  if (!token || query.length < 2) return []

  const params = new URLSearchParams({
    q: query,
    access_token: token,
    session_token: sessionToken,
    limit: '5',
    language: 'en',
    types: 'poi,address,place',
  })

  try {
    const res = await fetch(`https://api.mapbox.com/search/searchbox/v1/suggest?${params}`)
    if (!res.ok) return []
    const data = (await res.json()) as {
      suggestions?: {
        name: string
        place_formatted?: string
        full_address?: string
        mapbox_id: string
      }[]
    }
    return (data.suggestions ?? []).map((s) => ({
      placeName: s.name,
      address: s.place_formatted ?? s.full_address ?? '',
      mapboxId: s.mapbox_id,
    }))
  } catch {
    return []
  }
}

// Second step: retrieve coordinates for a selected suggestion
export async function retrieveGeocodeSuggestion(
  mapboxId: string,
  sessionToken: string,
): Promise<{ lat: number; lng: number; fullAddress: string } | null> {
  const token = MAPBOX_TOKEN
  if (!token) return null

  const params = new URLSearchParams({ access_token: token, session_token: sessionToken })

  try {
    const res = await fetch(
      `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(mapboxId)}?${params}`,
    )
    if (!res.ok) return null
    const data = (await res.json()) as {
      features?: {
        geometry: { coordinates: [number, number] }
        properties: { full_address?: string; name?: string; place_formatted?: string }
      }[]
    }
    const f = data.features?.[0]
    if (!f) return null
    return {
      lng: f.geometry.coordinates[0],
      lat: f.geometry.coordinates[1],
      fullAddress: f.properties.full_address ?? f.properties.name ?? '',
    }
  } catch {
    return null
  }
}

// Simple one-shot geocode for form submission fallback — keeps using v5 (address-only is fine here)
export async function geocodeAddress(
  address: string,
): Promise<{ lat: number; lng: number } | null> {
  const token = MAPBOX_TOKEN
  if (!token) return null
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}&limit=1`
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const data = (await res.json()) as { features?: { center: [number, number] }[] }
    const feature = data.features?.[0]
    if (!feature) return null
    return { lng: feature.center[0], lat: feature.center[1] }
  } catch {
    return null
  }
}
